import { useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { InfoIcon } from 'lucide-react';

import { DebounceSelect } from 'components/DebounceSelect';
import { Label } from 'components/Label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography/Typography';
import {
  DrumkitPlatform,
  SidebarStateContext,
} from 'contexts/sidebarStateContext';
import { useToast } from 'hooks/useToaster';
import {
  ThreadSearchResult,
  searchThreadsBySubject,
} from 'lib/api/findThreadBySubject';
import { Maybe } from 'types/UtilityTypes';

import { CheckInName } from './types';

export type ThreadSearchOption = ThreadSearchResult & {
  label: React.ReactNode;
  value: string;
};

export interface ThreadSubjectAutocompleteProps {
  name: CheckInName;
  disabled?: boolean;
}

export default function ThreadSubjectAutocomplete({
  name,
  disabled,
}: ThreadSubjectAutocompleteProps) {
  const { toast } = useToast();
  const { setValue, control } = useFormContext();
  const {
    currentState: { drumkitPlatform },
  } = useContext(SidebarStateContext);

  const watchedThreadID = useWatch({ control, name: `${name}.threadID` });
  const watchedReplyToSubject = useWatch({
    control,
    name: `${name}.replyToSubject`,
  });

  const handleThreadSelect = (
    value: Maybe<ThreadSearchOption>,
    option?: ThreadSearchOption
  ) => {
    if (!value || !option) {
      clearThreadSelection();
      return;
    }

    setValue(`${name}.threadID`, option.threadID);
    setValue(`${name}.replyToSubject`, option.subject);
    if (option.threadItemID) {
      setValue(`${name}.threadItemID`, option.threadItemID);
    }
    if (option.threadReferences) {
      setValue(`${name}.threadReferences`, option.threadReferences);
    }
    if (option.inReplyTo?.length) {
      setValue(`${name}.inReplyTo`, option.inReplyTo);
    }
    if (option.recipients?.length) {
      setValue(`${name}.recipients`, option.recipients);
    }

    toast({
      description: 'Thread selected successfully',
      variant: 'success',
    });
  };

  const clearThreadSelection = () => {
    setValue(`${name}.threadID`, undefined);
    setValue(`${name}.threadItemID`, undefined);
    setValue(`${name}.threadReferences`, undefined);
    setValue(`${name}.inReplyTo`, undefined);
    setValue(`${name}.replyToSubject`, undefined);
    setValue(`${name}.recipients`, undefined);
  };

  const threadSearchFetchOptions = async (search: string) => {
    const trimmed = search?.trim() ?? '';
    if (trimmed.length < 2) {
      return Promise.resolve([]);
    }

    const result = await searchThreadsBySubject(trimmed, 10);
    if (result.isErr()) {
      toast({
        description: result.error.message ?? 'Failed to search threads',
        variant: 'destructive',
      });
      return [];
    }

    return result.value.map((t: ThreadSearchResult) => ({
      label: t.subject,
      value: t.threadID,
      ...t,
    }));
  };

  const selectValue =
    watchedThreadID && watchedReplyToSubject !== undefined
      ? {
          label: watchedReplyToSubject || 'Selected thread',
          value: watchedThreadID,
        }
      : undefined;

  return (
    <div className='mt-2'>
      <Flex direction='row' justify='between' align='center' gap='xs'>
        <Label name={`${name}.replyToSubject`} hideAIHint>
          Thread subject
        </Label>

        {drumkitPlatform !== DrumkitPlatform.Front ? (
          <TooltipProvider>
            <Tooltip delayDuration={10}>
              <TooltipTrigger asChild>
                <InfoIcon className='w-4 h-4 text-neutral-500' />
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-64'>
                Thread subject search is limited to emails within the past 2
                weeks.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </Flex>
      <DebounceSelect
        className='mt-1 h-8 border-neutral-input-border text-[13px] [&_input]:bg-transparent!'
        placeholder='Search by subject (min 2 characters)'
        value={selectValue}
        fetchOptions={threadSearchFetchOptions}
        debounceTimeout={350}
        disabled={disabled}
        onChange={(value, option) => {
          if (value == null) {
            clearThreadSelection();
            return;
          }

          const isValidObject =
            typeof value === 'object' && value !== null && 'value' in value;
          const labelValue = isValidObject
            ? (value as ThreadSearchOption)
            : null;
          handleThreadSelect(labelValue, option as ThreadSearchOption);
        }}
        notFoundContent={
          <Typography variant='body-sm'>No threads found</Typography>
        }
        optionRender={(option) => (
          <Flex direction='col' gap='xs'>
            <Typography variant='body-sm'>
              {option?.label ?? option?.subject}
            </Typography>
            {option?.recipients?.length ? (
              <Typography variant='body-xs' className='text-neutral-500'>
                {option.recipients.join(', ')}
              </Typography>
            ) : null}
          </Flex>
        )}
      />
    </div>
  );
}
